import React, { useState, useEffect } from 'react';

import collabShirt from '../assets/images/common/collaboration-shirt.png'

import PageHeader from '../components/page-header';
import AmountCounter from '../components/amount-counter';
import ErrorInformation from '../components/error-information';
import ButtonFilled from '../components/button-filled';
import { useSelector, useDispatch } from 'react-redux';
import {
  removeOrderItem,
  addOrderItem,
  selectOrderItemAmount,
} from '../redux/orderItemAmountSlice';
import { selectWallet } from '../redux/walletSlice';
import { BrowserWallet } from '@meshsdk/core';
import { OpenTxid, PaymentTxid, UserDeadline } from '../constants/localStorageKeys';
import { selectStockItemsStats } from '../redux/stockItemsStatsSlice';
import { showToastError } from '../components/toastNotification';
import WaitingLoaderHover from './modals/waiting-loader-hover';
import { setSession } from '../redux/sessionSlice';
import { BackendApi, Socket } from './router-page';
import { selectConfig } from '../redux/configSlice';

const questions = [
  {
    question: 'Which shape has four sides?',
    options: ['Circle', 'Triangle', 'Octagon', 'Square'],
    correctAnswer: 'Square',
  },
  {
    question: 'On which planet do we live?',
    options: ['Jupiter', 'Mars', 'Mercury', 'Earth'],
    correctAnswer: 'Earth',
  },
  {
    question: 'How many legs does a cat have?',
    options: ['Two', 'Three', 'Four', 'Seven'],
    correctAnswer: 'Four',
  },
  {
    question: '7 + 8 = ?',
    options: ['2', '3', '4', '15'],
    correctAnswer: '15',
  },
];

const QuizPage = ({ handleNext, handlePrev, handleGoQueue, fetchStockStats }) => {
  const config = useSelector(selectConfig);

  const orderItemCount = useSelector(selectOrderItemAmount);
  const walletName = useSelector(selectWallet);
  const stockInfo = useSelector(selectStockItemsStats);
  const dispatch = useDispatch();

  const [totalPrice, setTotalPrice] = useState(0);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0);
  const [shuffledOptions, setShuffledOptions] = useState([]);
  const [userAnswer, setUserAnswer] = useState('');

  const [errorMessage, setErrorMessage] = useState(''); 
  const [isFundsWarning, setIsFundsWarning] = useState(false);
  const [isPendingWarning, setIsPendingWarning] = useState(false);
  const [isNotEnoughInStock, setIsNotEnoughInStock] = useState(false);

  const [isOrderCreating, setIsOrderCreating] = useState(false);

  useEffect(() => {
    const isNotEnoughInStock = (orderItemCount > stockInfo.inventory) && stockInfo.pending <= 0;
    const isPendingWarning = orderItemCount > stockInfo.inventory;
    setIsNotEnoughInStock(isNotEnoughInStock);
    setIsPendingWarning(isPendingWarning);

    if (isNotEnoughInStock) {
      setErrorMessage(<p>This collection's stock doesn't have as much items left</p>);
      return;
    }

    if (isPendingWarning) {
      setErrorMessage(<p>Now this collection's stock doesn't have as much items, but you can wait a bit, as there <span>{stockInfo.pending}</span> pending</p>);
      return;
    }

    if (isFundsWarning) {
      setErrorMessage('You do not have sufficient funds in your wallet.');
      return;
    }
    
    setErrorMessage('');
  }, [isFundsWarning, stockInfo, orderItemCount]);

  useEffect(() => {
    setIsNotEnoughInStock((orderItemCount > stockInfo.inventory) && stockInfo.pending <= 0);
    setIsPendingWarning(orderItemCount > stockInfo.inventory);

  }, [stockInfo, orderItemCount]);

  const handleIncrement = () => {
    dispatch(addOrderItem());
  };
  
  const handleDecrement = () => {
    dispatch(removeOrderItem());
  };

  async function refreshTotalPrice() {
    const newTotalPrice = orderItemCount * config.unitPrice;
    setTotalPrice(newTotalPrice);

    const wallet = await BrowserWallet.enable(walletName);
    const balance = await wallet.getBalance();
    const adaAmount = balance.find((item) => item.unit === 'lovelace').quantity / 1000000;

    const isFundsMiss = newTotalPrice > adaAmount; 
    setIsFundsWarning(isFundsMiss);
    return isFundsMiss;
  }

  useEffect(() => {
    
    refreshTotalPrice();
  }, [orderItemCount]);

  useEffect(() => {
    const randomQuestionIndex = Math.floor(Math.random() * questions.length);
    setSelectedQuestionIndex(randomQuestionIndex);

    const currentOptions = [...questions[randomQuestionIndex].options];
    const shuffled = [];

    while (currentOptions.length > 0) {
      const randomIndex = Math.floor(Math.random() * currentOptions.length);
      const removed = currentOptions.splice(randomIndex, 1);
      shuffled.push(removed[0]);
    }

    setShuffledOptions(shuffled);
  }, []);

  const handleAnswerClick = (selectedAnswer) => {
    setUserAnswer(selectedAnswer);
  };

  const createOrder = async () => {
    setIsOrderCreating(true);
    try {
      if (questions[selectedQuestionIndex].correctAnswer !== userAnswer) {
        showToastError({title: 'Quiz error', body: 'You selected wrong answer'});
        handlePrev();
        return;
      }

      await fetchStockStats();

      if (orderItemCount > stockInfo.inventory) 
        return;

      const isFundsMiss = await refreshTotalPrice();
      if (isFundsMiss) {
        showToastError({ title: 'Validation error', body: "Not enough funds" });
        return;
      }

      const address = await window.lucid.wallet.address();

      try {
        const response = await BackendApi.post(
          `/script/open`,
          {
            walletAddress: address,
            amount: orderItemCount
          }
        );
        
        if (response.data.success) {
          if (response.data.data.session)
            dispatch(setSession(response.data.data.session));
          handleNext();
          localStorage.setItem(UserDeadline, new Date().getTime() + response.data.data.timeForUser * 60 * 1000);
          Socket.emit("setOpenOrderInfo", { openTxid: response.data.data.txid });
          localStorage.setItem(OpenTxid, response.data.data.txid);
          localStorage.removeItem(PaymentTxid);
        } else {
          showToastError({ title: 'Validation error', body: response.data.message });
        } 
      } catch (error) {
        if (error.response.status === 500) {
          handleGoQueue();
        }
      }
    }
    catch {}
    finally {
      setIsOrderCreating(false);
    }
  }

  return (
    <section className='page-container'>
      <div className='order-page quiz-stage'>

        <div className='center-items-horizontal'>
          <PageHeader currentStepNumber={2}/>

          <div className='quiz-content'>
            <p className='question'>{questions[selectedQuestionIndex].question}</p>

            <form className='answers-block'>
              {shuffledOptions.map((option, index) => (
                <label className='answer custom-radio' key={index}>
                  <p className='text'>{option}</p>
                  <input
                    type="radio"
                    name="answerOptions"
                    value={option}
                    checked={userAnswer === option}
                    onChange={() => handleAnswerClick(option)}
                  />
                </label>
              ))}
            </form>
          </div>

          <div className='order-item-quiz-stage'>
            <img src={collabShirt} alt='Collab jersey'/>
            <p>LVLCS x World Mobile Token Football Jersey</p>
            <AmountCounter count={orderItemCount} onIncrement={handleIncrement} onDecrement={handleDecrement} />
          </div>

          <div className='total-price-quiz-stage'>
            <p>This will cost you <span>{totalPrice}</span> ADA.</p>
          </div>

          {errorMessage && <ErrorInformation information={errorMessage}/>}
        </div>

        <div className='navigation-block-buttons'>
          <ButtonFilled label='BACK' palette='secondary' size='regular' customStyles={{width: 254}} onClick={handlePrev}/>
          <ButtonFilled label='NEXT' palette='primary' size='regular' customStyles={{width: 254}} onClick={createOrder} disabled={isFundsWarning || isPendingWarning || isNotEnoughInStock || !userAnswer || isOrderCreating}/>
        </div>
      </div>
      <WaitingLoaderHover isOpen={isOrderCreating}/>
    </section>
  );
};

export default QuizPage;